import { DateTime } from "luxon";
import React, { useCallback, useEffect, useState } from "react";
import { dynamicsService } from "../services/DynamicsService";
import Col100 from "./Col100";
import Header from "./Header";

type Props = {
  opportunityId: string;
};

function useTimeout(callback: any, delay: number | undefined) {
  const timeoutRef = React.useRef<number>();
  const callbackRef = React.useRef(callback);

  // Remember the latest callback:
  //
  // Without this, if you change the callback, when setTimeout kicks in, it
  // will still call your old callback.
  //
  // If you add `callback` to useEffect's deps, it will work fine but the
  // timeout will be reset.

  React.useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Set up the timeout:

  React.useEffect(() => {
    if (delay) {
      timeoutRef.current = window.setTimeout(() => callbackRef.current(), delay);

      // Clear timeout if the components is unmounted or the delay changes:
      return () => window.clearTimeout(timeoutRef.current);
    }
  }, [delay]);

  // In case you want to manually clear the timeout from the consuming component...:
  return timeoutRef;
}

export default function Payment(props: Props): JSX.Element {
  const { opportunityId } = props;
  // Local state
  const [fetchDelay, setFetchDelay] = useState<number>(2000);
  const [error, setError] = useState<string>();
  const [assessmentDate, setAssessmentDate] = useState<DateTime>();
  const [paid, setPaid] = useState<boolean>();
  const [confirmed, setConfirmed] = useState<boolean>();
  const [assigned, setAssigned] = useState<boolean>();

  // Accessors

  // Event Handlers
  const fetchOpportunity = useCallback(() => {
    // console.log("fetchOpportunity");
    dynamicsService.getOpportunityStatus(opportunityId).then((opportunityResponse) => {
      console.log(opportunityResponse);
      if (opportunityResponse.statusId === 876960001) {
        setPaid(true);
      }
      if (opportunityResponse.assessmentDate) {
        setConfirmed(true);
        setAssessmentDate(DateTime.fromISO(opportunityResponse.assessmentDate));
      }
      if (opportunityResponse.assessorId) {
        setAssigned(true);
      }
      setFetchDelay((value) => value * 2);
    })
    .catch((reason) => {
      setError(reason);
      setFetchDelay(0);
    })}, [opportunityId]);

  useEffect(() => {
    fetchOpportunity();
  }, [fetchOpportunity]);

  useTimeout(() => fetchOpportunity(), (paid && assigned && confirmed && fetchDelay) ? undefined : fetchDelay);

  if (error) {
    return (
      <Col100>
        <strong>ERROR!</strong> {error}
      </Col100>
    );
  }

  const unknown = () => <i className="far fa-fw fa-square" />;
  const done = () => <i className="fa far fa-fw fa-check-square" />;
  const checking = () => <i className="fa fa-fw fa-spin fa-sync-alt" />;

  return <Col100>
      <Header>Confirming Your First Step...</Header>
      <div className="elementor-element elementor-widget elementor-widget-heading" data-id="a595307" data-element_type="widget" data-widget_type="heading.default">
        <div className="elementor-widget-container">
          <h3 className="elementor-heading-title elementor-size-default">
            Thank you for joining. Your appointment details are
            as follows:
          </h3>
        </div>
      </div>
      {assessmentDate &&
      <div className="elementor-widget-container pb-1">
          <h3 className="elementor-heading-title elementor-size-default">
            {assessmentDate.toLocal().toLocaleString(DateTime.TIME_SIMPLE)}<br />
            {assessmentDate.toLocal().toLocaleString(DateTime.DATE_HUGE)}
          </h3>
        </div>
      }
      {(!paid || !confirmed || !assigned) && <div className="elementor-element elementor-widget elementor-widget-text-editor" data-element_type="widget" data-widget_type="text-editor.default">
        <div className="elementor-widget-container">
          <ul className="elementor-icon-list-items">
            <li className="elementor-icon-list-item">
              <span className="elementor-icon-list-icon">
                {paid ? done() : checking()}
              </span>
              <span className="elementor-icon-list-text">Membership Information {paid ? <>Complete</> : <>(Verifying)</>}
              </span>
            </li>
            <li className="elementor-icon-list-item">
              <span className="elementor-icon-list-icon">
                {paid ? (confirmed ? done() : checking()) : unknown()}
              </span>
              <span className="elementor-icon-list-text">
                Virtual First Visit {paid ? (confirmed ? <>Confirmed</> : <>(Confirming)</>) : <></>}
              </span>
            </li>
            <li className="elementor-icon-list-item">
              <span className="elementor-icon-list-icon">
                {confirmed ? (assigned ? done() : checking()) : unknown()}
              </span>
              <span className="elementor-icon-list-text">
                SafeHaven RN Guide {confirmed ? (assigned ? <>Assigned</> : <>(Searching)</>) : <></>}
              </span>
            </li>
          </ul>
        </div>
      </div>}
      <div className="elementor-element elementor-widget elementor-widget-text-editor pt-3" data-element_type="widget" data-widget_type="text-editor.default">
        <div className="elementor-widget-container">
          <div className="elementor-text-editor elementor-clearfix">
            <p>
              A confirmation email with complete details will be sent to you.
              You will also receive a reminder email 48 hours before your
              appointment.
            </p>
          </div>
        </div>
      </div>
    </Col100>;

  return (
    <Col100>
      <Header>Scheduled!</Header>
      <div
        className="elementor-element elementor-widget elementor-widget-heading"
        data-id="a595307"
        data-element_type="widget"
        data-widget_type="heading.default"
      >
        <div className="elementor-widget-container">
          <h3 className="elementor-heading-title elementor-size-default">
            Thank you for choosing DwellAssured. Your appointment details are as
            follows:
          </h3>
        </div>
      </div>
      <div
        className="elementor-element elementor-widget elementor-widget-text-editor"
        data-element_type="widget"
        data-widget_type="text-editor.default"
      >
        <div className="elementor-widget-container">
          <div className="elementor-text-editor elementor-clearfix">
            <p>
              A confirmation email with complete details will be sent to you.
              You will also receive a reminder email 48 hours before your
              apoointment time, as well as a courtesy call to the primary
              contact phone number you provided 30 minutes before your
              assessor's arrival.
            </p>
          </div>
        </div>
      </div>
      <div className="elementor-row">
        <div
          className="elementor-column elementor-col-50 elementor-inner-column elementor-element elementor-element-a07ec27"
          data-id="a07ec27"
          data-element_type="column"
        >
          <div className="elementor-column-wrap elementor-element-populated">
            <div className="elementor-widget-wrap">
              <div
                className="elementor-element elementor-element-7f7ad0c elementor-align-center elementor-widget elementor-widget-button"
                data-id="7f7ad0c"
                data-element_type="widget"
                data-widget_type="button.default"
              >
                <div className="elementor-widget-container">
                  <div className="elementor-button-wrapper">
                    <a
                      href="#"
                      className="elementor-button-link elementor-button elementor-size-sm"
                      role="button"
                    >
                      <span className="elementor-button-content-wrapper">
                        <span className="elementor-button-icon elementor-align-icon-left">
                          <i
                            aria-hidden="true"
                            className="fas fa-calendar-alt"
                          />
                        </span>
                        <span className="elementor-button-text">
                          Add to Calendar
                        </span>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="elementor-column elementor-col-50 elementor-inner-column elementor-element elementor-element-a07ec27"
          data-id="a07ec27"
          data-element_type="column"
        >
          <div className="elementor-column-wrap elementor-element-populated">
            <div className="elementor-widget-wrap">
              <div
                className="elementor-element elementor-element-7f7ad0c elementor-align-center elementor-widget elementor-widget-button"
                data-id="7f7ad0c"
                data-element_type="widget"
                data-widget_type="button.default"
              >
                <div className="elementor-widget-container">
                  <div className="elementor-button-wrapper">
                    <a
                      href="#"
                      className="elementor-button-link elementor-button elementor-size-sm"
                      role="button"
                    >
                      <span className="elementor-button-content-wrapper">
                        <span className="elementor-button-icon elementor-align-icon-left">
                          <i aria-hidden="true" className="fas fa-share" />
                        </span>
                        <span className="elementor-button-text">
                          Share My Appointment
                        </span>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Col100>
  );
}
